import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { STATUS_OPTIONS, statusKey } from '@shared/domain/status.ts';
import { SIMPLE_STATUSES } from '@shared/domain/flow.ts';
import { formatHoursValue, secondsToHours } from '@shared/domain/hours.ts';
import { formatMinutes, roundMinutes } from '@shared/domain/rounding.ts';
import { localDate } from '@shared/domain/time.ts';
import { store, useApp } from '../state/store.ts';
import { formatCalendarDate, formatElapsed, formatFechas, formatInstant } from '../lib/format.ts';
import { comparePeople, peopleIndex, personInfo } from '../lib/people.ts';
import type { CardListItem, WorkLog } from '../lib/types.ts';
import {
  Avatar,
  BackLink,
  BodyView,
  PersonChip,
  SheetDialog,
  StatusChip,
  TagChip,
  useNow,
} from '../components/common.tsx';

function TimerControl({ card }: { card: CardListItem }) {
  const { openEntry } = useApp();
  const now = useNow(1000);
  const running = openEntry?.card_id === card.id;
  if (running && openEntry) {
    return (
      <div className="timer-control">
        <span className="timer-elapsed big">
          {formatElapsed((now.getTime() - Date.parse(openEntry.started_at)) / 1000)}
        </span>
        <button
          type="button"
          className="button button-danger"
          onClick={() => void store.stopTimer()}
        >
          Detener cronómetro
        </button>
      </div>
    );
  }
  return (
    <div className="timer-control">
      <button
        type="button"
        className="button button-primary"
        disabled={card.is_closed}
        onClick={() => void store.startTimer(card)}
      >
        Iniciar cronómetro
      </button>
      {card.is_closed && (
        <p className="muted small">
          El cronómetro no está disponible en tarjetas cerradas. Puedes registrar las horas.
        </p>
      )}
      {!card.is_closed && openEntry && (
        <p className="muted small">
          Iniciarlo detiene el cronómetro que tienes en curso y suma ese tiempo a su registro.
        </p>
      )}
    </div>
  );
}

/** Status con nombres simples; «Más estados» muestra la lista completa de Notion. */
function StatusPicker({ card }: { card: CardListItem }) {
  const current = statusKey(card.status);
  const simple = SIMPLE_STATUSES.some((s) => statusKey(s.status) === current);
  const [all, setAll] = useState(!simple);
  return (
    <fieldset className="status-picker">
      <legend>Status</legend>
      <div className="status-options">
        {SIMPLE_STATUSES.map((s) => (
          <button
            key={s.status}
            type="button"
            className="button status-option"
            aria-pressed={statusKey(s.status) === current}
            onClick={() => void store.updateCard(card.id, { status: s.status })}
          >
            {s.label}
          </button>
        ))}
      </div>
      {all ? (
        <label>
          Todos los estados de Notion
          <select
            value={card.status}
            onChange={(e) => void store.updateCard(card.id, { status: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.name} value={o.name}>
                {o.name}
              </option>
            ))}
            {!STATUS_OPTIONS.some((o) => o.name === card.status) && (
              <option value={card.status}>{card.status}</option>
            )}
          </select>
        </label>
      ) : (
        <button type="button" className="link-button" onClick={() => setAll(true)}>
          Más estados
        </button>
      )}
    </fieldset>
  );
}

function PropertyEditor({ card }: { card: CardListItem }) {
  const { notionUsers, people } = useApp();
  const members = useMemo(() => {
    const index = peopleIndex(notionUsers, people);
    const ids = new Set([...index.keys(), ...card.miembros]);
    return [...ids].map((id) => personInfo(index, id)).sort(comparePeople);
  }, [notionUsers, people, card.miembros]);
  const [start, setStart] = useState(card.fechas?.start.slice(0, 10) ?? '');
  const [end, setEnd] = useState(card.fechas?.end?.slice(0, 10) ?? '');
  const [hours, setHours] = useState(
    card.horas_estimadas === null ? '' : String(card.horas_estimadas),
  );

  return (
    <div className="props-edit">
      <StatusPicker card={card} />

      <fieldset className="members">
        <legend>Miembros</legend>
        {members.map((p) => (
          <label key={p.id} className={`member-option${p.named ? '' : ' muted'}`}>
            <input
              type="checkbox"
              checked={card.miembros.includes(p.id)}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...card.miembros, p.id]
                  : card.miembros.filter((m) => m !== p.id);
                void store.updateCard(card.id, { miembros: next });
              }}
            />
            <Avatar person={p} decorative />
            {p.label}
          </label>
        ))}
        {members.some((p) => !p.named) && (
          <p className="muted small members-note">
            Notion no entrega el nombre de algunas personas (por ejemplo, quienes ya no están en el
            workspace); se muestran con el final de su id. Si se vinculan a Tablero, aparece el
            nombre de su cuenta.
          </p>
        )}
      </fieldset>

      <div className="grid-2">
        <label>
          Fecha de inicio
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label>
          Fecha de término
          <input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
          />
        </label>
      </div>
      <div className="row">
        <button
          type="button"
          className="button"
          disabled={!start}
          onClick={() => void store.updateCard(card.id, { fechas: { start, end: end || null } })}
        >
          Guardar fechas
        </button>
        {card.fechas && (
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setStart('');
              setEnd('');
              void store.updateCard(card.id, { fechas: null });
            }}
          >
            Quitar fechas
          </button>
        )}
      </div>

      <div className="row">
        <label>
          Horas estimadas
          <input
            type="number"
            min={0}
            step={0.25}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="button"
          onClick={() =>
            void store.updateCard(card.id, { horas_estimadas: hours === '' ? null : Number(hours) })
          }
        >
          Guardar horas estimadas
        </button>
      </div>
    </div>
  );
}

/** «···»: Status simple, Miembros, Fechas, Horas estimadas, el enlace a Notion y los detalles. */
function CardOptions({ card, onClose }: { card: CardListItem; onClose: () => void }) {
  const { notionUsers, people } = useApp();
  const index = useMemo(() => peopleIndex(notionUsers, people), [notionUsers, people]);
  return (
    <SheetDialog
      title="Opciones de la tarjeta"
      subtitle={card.name || '(sin nombre)'}
      onClose={onClose}
      testId="card-options"
    >
      <PropertyEditor key={`${card.id}-${card.notion_last_edited}`} card={card} />
      <p>
        <a href={card.notion_url} target="_blank" rel="noreferrer noopener">
          Abrir en Notion
        </a>
      </p>
      <dl className="props">
        <dt>Status en Notion</dt>
        <dd>{card.status}</dd>
        <dt>Etiqueta</dt>
        <dd>
          {card.etiquetas.length ? card.etiquetas.map((t) => <TagChip key={t} tag={t} />) : '—'}
        </dd>
        <dt>Marca</dt>
        <dd>{card.marca ?? '—'}</dd>
        <dt>Tipo</dt>
        <dd>{card.tipo ?? '—'}</dd>
        <dt>Proyecto</dt>
        <dd>{card.proyecto ?? '—'}</dd>
        <dt>Líder del proyecto</dt>
        <dd>
          {card.lider_proyecto.length
            ? card.lider_proyecto.map((pid) => (
                <PersonChip key={pid} person={personInfo(index, pid)} />
              ))
            : '—'}
        </dd>
        <dt>Fecha de creación</dt>
        <dd>{formatInstant(card.fecha_creacion)}</dd>
        <dt>Última edición en Notion</dt>
        <dd>{formatInstant(card.notion_last_edited)}</dd>
      </dl>
    </SheetDialog>
  );
}

/** Un registro por persona y día: cuánto se trabajó (sin hora). */
function LogRow({ log, canEdit, person }: { log: WorkLog; canEdit: boolean; person?: string }) {
  const { userId } = useApp();
  return (
    <li className="entry" data-testid="work-log">
      <div className="entry-main">
        <span className="entry-date">{formatCalendarDate(log.work_date)}</span>
        <strong>{formatHoursValue(secondsToHours(log.seconds))}</strong>
        {person && <span className="muted">{person}</span>}
        <span className="badges">
          {log.needs_review && <span className="badge badge-warn">Revisar</span>}
          {log.pending && <span className="badge badge-pending">Pendiente</span>}
        </span>
      </div>
      {canEdit && (
        <div className="row">
          <button
            type="button"
            className="link-button"
            onClick={() =>
              store.openHoursSheet({
                cardId: log.card_id,
                withTimer: false,
                date: log.work_date,
                userId: log.user_id === userId ? undefined : log.user_id,
              })
            }
          >
            Editar
          </button>
          <button
            type="button"
            className="link-button danger"
            onClick={() => {
              if (window.confirm('¿Borrar este registro de horas?')) void store.deleteWorkLog(log);
            }}
          >
            Borrar
          </button>
        </div>
      )}
    </li>
  );
}

export function CardPage() {
  const { id = '' } = useParams();
  const { cards, details, logsByCard, profile, people, userId, online } = useApp();
  const [options, setOptions] = useState(false);

  useEffect(() => {
    void store.openCard(id);
  }, [id]);

  const card: CardListItem | undefined = details[id] ?? cards.find((c) => c.id === id);
  const detail = details[id];
  const logs = useMemo(() => logsByCard[id] ?? [], [logsByCard, id]);
  const personName = useMemo(
    () => new Map(people.map((p) => [p.id, p.display_name ?? p.email])),
    [people],
  );

  if (!card) {
    return (
      <div>
        <p>Esta tarjeta no está en la lista (puede estar cerrada hace más de 60 días).</p>
        <Link to="/">Volver a Mis tareas</Link>
      </div>
    );
  }

  const rounding = profile?.redondeo ?? 'ninguno';
  const totalSeconds = logs.reduce((s, l) => s + l.seconds, 0);
  const isAdmin = profile?.role === 'admin';
  const mine = logs.filter((l) => l.user_id === userId);
  const others = logs.filter((l) => l.user_id !== userId);
  const mineSeconds = mine.reduce((s, l) => s + l.seconds, 0);
  const today = mine.find((l) => l.work_date === localDate(new Date()));

  return (
    <article className="card-page">
      <BackLink />
      <div className="card-head">
        <h1>{card.name || '(sin nombre)'}</h1>
        <button
          type="button"
          className="icon-button card-options-button"
          aria-label="Más opciones"
          aria-haspopup="dialog"
          onClick={() => setOptions(true)}
        >
          ···
        </button>
      </div>
      <p className="row card-status">
        <StatusChip status={card.status} group={card.status_group} />
        {card.fechas && <span className="muted small">{formatFechas(card.fechas)}</span>}
      </p>

      <section className="panel" aria-label="Qué hay que hacer">
        <h2>Qué hay que hacer</h2>
        {detail?.body ? (
          detail.body.length > 0 ? (
            <BodyView blocks={detail.body} />
          ) : (
            <p className="muted">Sin contenido.</p>
          )
        ) : online ? (
          <p className="muted">Cargando contenido…</p>
        ) : (
          <p className="muted">
            El contenido se descarga la primera vez que abres la tarjeta con conexión.
          </p>
        )}
        {(card.figma_dev_mode || card.figma_prototipo) && (
          <p className="row">
            {card.figma_dev_mode && (
              <a href={card.figma_dev_mode} target="_blank" rel="noreferrer noopener">
                Figma DEV Mode
              </a>
            )}
            {card.figma_prototipo && (
              <a href={card.figma_prototipo} target="_blank" rel="noreferrer noopener">
                Figma Prototipo
              </a>
            )}
          </p>
        )}
      </section>

      <section className="card-actions" aria-label="Horas">
        <div className="row hours-actions">
          <button
            type="button"
            className="button button-primary"
            onClick={() => store.openHoursSheet({ cardId: card.id, withTimer: false })}
          >
            Registrar horas
          </button>
          <TimerControl card={card} />
        </div>
        {today && (
          <p className="muted small" data-testid="card-today">
            Hoy llevas {formatHoursValue(secondsToHours(today.seconds))} en esta tarjeta.
          </p>
        )}
      </section>

      <details className="panel logs">
        <summary>
          <h2>
            Mis registros{' '}
            <span className="count">
              {mine.length === 0 ? 'sin registros' : formatHoursValue(secondsToHours(mineSeconds))}
            </span>
          </h2>
        </summary>
        <ul className="entries" aria-label="Mis registros">
          {mine.length === 0 && <li className="muted">Sin registros.</li>}
          {mine.map((l) => (
            <LogRow key={l.work_date} log={l} canEdit />
          ))}
        </ul>
        {others.length > 0 && (
          <>
            <h3 className="small">Del equipo</h3>
            <ul className="entries" aria-label="Registros del equipo">
              {others.map((l) => (
                <LogRow
                  key={`${l.user_id}|${l.work_date}`}
                  log={l}
                  canEdit={isAdmin}
                  person={personName.get(l.user_id) ?? 'Otra persona'}
                />
              ))}
            </ul>
          </>
        )}
        <p className="muted small">
          Total de la tarjeta:{' '}
          <strong data-testid="card-total">
            {formatMinutes(roundMinutes(totalSeconds, rounding))}
          </strong>
          {rounding === '15min' && ' (redondeado a 15 min)'} · Horas trabajadas en Notion:{' '}
          <strong>{card.horas_trabajadas ?? '—'}</strong>
        </p>
      </details>

      {options && <CardOptions card={card} onClose={() => setOptions(false)} />}
    </article>
  );
}
